/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {tap} from 'rxjs/operators';
import {Cart} from '../interfaces/Cart';
import {CartProduct} from '../interfaces/CartProduct';
import {CartProductReplace} from '../interfaces/CartProductReplace';
import {CustomProduct} from '../interfaces/CustomProduct';
import {Environment} from '../interfaces/Environment';
import {ProductCustomer} from '../interfaces/ProductCustomer';
import {CartBaseService} from './cart-base.service';
import {CartEventService} from './cart-event.service';
import {StoreService} from './store.service';

@Injectable({
  providedIn: 'root'
})

export class CartLocalService extends CartBaseService implements Cart {

  constructor(protected readonly storeService: StoreService,
              protected readonly cartEventService: CartEventService,
              @Inject('environment') protected readonly environment: Environment) {
    super(cartEventService, environment);
    this.init();
  }

  addCustom(product: CustomProduct): Observable<CartProduct> {

    return this.pushCustom(product).pipe(
      tap(resp => this.doNextCartProduct(resp)),
      tap(_ => this.doNext())
    );
  }

  init(): void {
    this.get().pipe(tap(resp => this.cart = resp));
  }

  add(product: ProductCustomer, amount: number): Observable<CartProduct> {

    if (amount <= 0) {
      return of<CartProduct>();
    }

    return this.push(product, amount).pipe(
      tap(resp => this.doNextCartProduct(resp)),
      tap(_ => this.doNext())
    );
  }

  addList(cartProductList: CartProduct[]): Observable<CartProduct[]> {
    return of<CartProduct[]>(cartProductList);
  }

  clear(): Observable<any> {
    this.storeService.delCart();
    return super.clear();
  }

  get(): Observable<CartProduct[]> {
    return of(this.getCart()).pipe(tap(_ => this.doNext()));
  }

  getShareCart(): Observable<CartProduct[]> {
    return of(this.getCart()).pipe(tap(_ => this.doNext()));
  }

  getByRegion(region: number): Observable<CartProduct[]> {
    return this.get();
  }

  remove(cartProduct: CartProduct): Observable<any> {

    const index = this.findIndex(cartProduct.id);

    if (index > -1) {
      this.cart.splice(index, 1);
      this.storeService.setCart(this.cart);
    }

    return of({}).pipe(tap(_ => this.doNext()));
  }

  getCart(): CartProduct[] {
    if (this.cart === undefined || this.cart === null) {
      this.cart = this.getStoreCart();
    }
    return this.cart;
  }

  getStoreCart(): CartProduct[] {
    return this.storeService.getCart();
  }

  protected push(product: ProductCustomer, amount: number): Observable<CartProduct> {

    const index = this.findIndex(product.syncUid);

    const cartProduct = index === -1 ? this.createCartProduct(product, amount) : this.cart[index];

    if (index === -1) {
      this.cart.push(cartProduct);
    } else {
      cartProduct.amount = cartProduct.amount + amount;
      this.cart[index] = cartProduct;
    }

    this.storeService.setCart(this.cart);

    return of(cartProduct);
  }

  protected pushCustom(product: CustomProduct): Observable<CartProduct> {

    const cartProduct = this.createCustomCartProduct(product);
    this.cart.push(cartProduct);

    this.storeService.setCart(this.cart);

    return of(cartProduct);
  }

  recount(cartProduct: CartProduct, amount: number): Observable<CartProduct> {
    return this._recount(cartProduct, amount).pipe(
      tap(resp => this.doNextCartProduct(resp)),
      tap(_ => this.doNext())
    );

  }

  protected _recount(cartProduct: CartProduct, amount: number): Observable<CartProduct> {
    const index = this.findIndex(cartProduct.id);

    if (index !== -1) {
      cartProduct.amount = amount;
      this.cart[index] = cartProduct;
      this.storeService.setCart(this.cart);
    }

    return of(cartProduct);
  }

  replace(cartProductReplace: CartProductReplace): Observable<any> {
    return null;
  }

}
